import React from 'react';
import { Box, ButtonBase, Skeleton, Stack, Typography } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import ErrorOutlineRounded from '@mui/icons-material/ErrorOutlineRounded';
import PendingActionsRounded from '@mui/icons-material/PendingActionsRounded';
import TaskAltRounded from '@mui/icons-material/TaskAltRounded';

export interface AttentionItem {
  key: string;
  /** What waits, with its count: "2 replies to check". */
  label: string;
  /** Whose it is: an agent's name. */
  detail?: string | null;
  /** `problem` for something that stopped working, rather than work waiting. */
  tone?: 'waiting' | 'problem';
  /** Opens the list that holds it. */
  onOpen?: () => void;
}

export interface AttentionListProps {
  items: AttentionItem[];
  loading?: boolean;
  /** Said, calmly, when nothing waits: "Nothing needs you right now." */
  emptyMessage?: string;
}

/**
 * What waits on a person, across everything on the page: replies to check,
 * files to confirm, a job that stopped. Each opens where it can be done.
 *
 * A problem is drawn apart from work waiting, so it is never read as a
 * routine count. With nothing waiting it says so in one quiet line.
 */
export function AttentionList({ items, loading = false, emptyMessage = 'Nothing needs you right now.' }: AttentionListProps) {
  if (loading && !items.length) {
    return (
      <Stack direction="row" spacing={1} aria-hidden sx={{ flexWrap: 'wrap' }} useFlexGap>
        {[0, 1].map((i) => <Skeleton key={i} variant="rounded" width={220} height={44} />)}
      </Stack>
    );
  }
  if (!items.length) {
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <TaskAltRounded fontSize="small" sx={(t) => ({ color: t.tokens.semantic.success })} />
        <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary })}>{emptyMessage}</Typography>
      </Stack>
    );
  }
  return (
    <Box component="ul" sx={{
      m: 0, p: 0, listStyle: 'none', display: 'grid', gap: 1,
      // A few items stretch across the row rather than leaving it half empty.
      gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fit, minmax(260px, 1fr))' },
    }}>
      {items.map((item) => {
        const problem = item.tone === 'problem';
        const Icon = problem ? ErrorOutlineRounded : PendingActionsRounded;
        const body = (
          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ width: '100%', minWidth: 0, textAlign: 'left' }}>
            <Icon fontSize="small" sx={(t) => ({
              color: problem ? t.tokens.semantic.danger : t.tokens.semantic.warning, flexShrink: 0,
            })} />
            <Stack spacing={0} sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="body2" component="span" sx={(t) => ({ color: t.tokens.text.primary, fontWeight: 600 })}>
                {item.label}
              </Typography>
              {item.detail ? (
                <Typography variant="caption" component="span" sx={(t) => ({ color: t.tokens.text.tertiary })}>
                  {item.detail}
                </Typography>
              ) : null}
            </Stack>
            {item.onOpen ? <ChevronRightRounded fontSize="small" sx={(t) => ({ color: t.tokens.text.tertiary })} /> : null}
          </Stack>
        );
        const frame = (t: Theme) => ({
          width: '100%', px: 1.5, py: 1, borderRadius: `${t.ds.radius.sm}px`,
          border: `1px solid ${problem ? t.tokens.semantic.danger : t.tokens.border.default}`,
          bgcolor: problem ? t.tokens.semantic.dangerSoft : t.tokens.semantic.warningSoft,
        });
        return (
          <Box component="li" key={item.key} sx={{ display: 'flex' }}>
            {item.onOpen ? (
              <ButtonBase onClick={item.onOpen} sx={(t) => ({
                ...frame(t),
                '&:hover': { borderColor: t.tokens.border.strong },
                '&.Mui-focusVisible': { boxShadow: t.tokens.shadow.focusRing },
              })}>
                {body}
              </ButtonBase>
            ) : <Box sx={frame}>{body}</Box>}
          </Box>
        );
      })}
    </Box>
  );
}
